'use client';

import Select from 'react-select';
import { Controller } from 'react-hook-form';
import { useEffect, useState } from 'react';

const SelectDropdown = ({ name, control, options = [], label, placeholder = 'Select...', isRequired = false, required = false, isDisabled = false, isBorderless = false, className = '', ...rest }) => {
  const [mounted, setMounted] = useState(false);
  const isFieldRequired = isRequired || required;

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <select className="form-input w-full" disabled>
        <option>Loading...</option>
      </select>
    );
  }

  // Common props for the Select component
  const selectProps = {
    inputId: name,
    options,
    placeholder,
    className: 'react-select-container',
    classNamePrefix: 'react-select',
    isClearable: true,
    menuPosition: 'fixed',
    menuPlacement: 'auto',
    isDisabled: isDisabled || rest.isLoading,
    styles: {
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      clearIndicator: (base) => ({
        ...base,
        padding: '2px',
        width: '18px',
        height: '18px',
        svg: {
          width: '18px',
          height: '18px',
          marginBottom: '2px',
        },
      }),
      dropdownIndicator: (base) => ({
        ...base,
        width: '18px',
        height: '18px',
        svg: {
          width: '18px',
          height: '18px',
          marginBottom: '2px',
        },
      }),
      indicatorsContainer: (base) => ({
        ...base,
        gap: '1px',
      }),
    },
    classNames: {
      control: (state) =>
        `react-select__control
       ${state.isDisabled ? '!bg-gray-100 !text-gray-400 dark:!bg-gray-700 dark:!text-gray-400' : ''} 
       ${rest.error ? '!border-red-500' : ''}
       ${isBorderless ? '!border-none !shadow-none !outline-none !ring-0 focus:!ring-0 focus:!shadow-none' : ''}`,
      menu: () => 'react-select__menu',
      menuList: () => 'react-select__menu-list',
      option: (state) =>
        `react-select__option 
       ${state.isFocused ? 'react-select__option--is-focused' : ''} 
       ${state.isSelected ? 'react-select__option--is-selected' : ''}`,
      singleValue: (state) =>
        `react-select__single-value 
       ${state.isDisabled ? '!text-gray-500 dark:!text-gray-400' : ''}`,
      placeholder: (state) =>
        `react-select__placeholder 
       ${state.isDisabled ? '!text-gray-400 dark:!text-gray-500' : 'dark:!text-white'}`,
      indicatorSeparator: (state) =>
        `react-select__indicator-separator 
       ${state.isDisabled ? '!bg-gray-300 dark:!bg-gray-600 ' : ''}`,
      dropdownIndicator: (state) =>
        `react-select__dropdown-indicator 
       ${state.isDisabled ? '!text-gray-400 !display-none' : ''}`,
      valueContainer: () => 'react-select__value-container',
      input: () => 'react-select__input absolute    ',
    },
  };

  const resolveOption = (val) => {
    if (val == null) return null;
    if (typeof val === 'object') {
      const id = val.id ?? val.value;
      const target = id != null ? String(id) : null;
      return options.find((o) => String(o.value) === target) || null;
    }

    const byValue = options.find((o) => String(o.value) === String(val));
    if (byValue) return byValue;
    const t = String(val).toLowerCase();
    return options.find((o) => String(o.label).toLowerCase() === t) || null;
  };

  if (!control) {
    const current = resolveOption(rest.value);

    const handleChange = (selected) => {
      if (typeof rest.onChange === 'function') rest.onChange(selected || null);
    };

    return (
      <div className={`w-full ${className}`}>
        <div className="flex items-center gap-1 mb-1">
          <label htmlFor={name} className="text-xs font-semibold text-text-100 dark:text-dark-text-100">
            {label}
          </label>
          {isFieldRequired && <span className="text-red-500 text-sm">*</span>}
        </div>
        <Select {...selectProps} {...rest} value={current} onChange={handleChange} isDisabled={isDisabled || rest.isLoading} />
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-center gap-1 mb-1">
        <label htmlFor={name} className="text-xs font-semibold text-text-100 dark:text-dark-text-100">
          {label}
        </label>
        {isFieldRequired && <span className="text-red-500 text-sm">*</span>}
      </div>
      <Controller
        name={name}
        control={control}
        rules={{
          required: isFieldRequired ? 'Required' : false,
        }}
        render={({ field, fieldState: { error } }) => (
          <div className="w-full">
            <Select
              {...selectProps}
              {...rest}
              {...field}
              value={resolveOption(field.value)}
              onChange={(selected) => field.onChange(selected || null)}
              onBlur={field.onBlur}
              isDisabled={isDisabled || rest.isLoading}
            />
            {error && <p className="mt-1 text-xs text-red-500">{error.message}</p>}
          </div>
        )}
      />
    </div>
  );
};

export default SelectDropdown;
